import { isForecastEnabled } from '@/lib/feature-flags';
/**
 * Finance-nav memory (F-013) — remember the builder's last finance view so the
 * sidebar "Tracking" entry reopens where they were, not always Overview.
 *
 * Deliberately its OWN localStorage key, separate from nav-tracker's global
 * `fb-last-route` (that one tracks the whole app; this one is finance-scoped).
 * Client-only: every access is try/caught for private-browsing / SSR safety.
 *
 * `readLastFinanceView` returns a value from a fixed allow-list, so callers can
 * pass it straight to a typed router `to` — it can never be an arbitrary/foreign
 * path (no open-redirect, and it type-checks against the route union).
 */

export const FINANCE_ROUTES = [
  '/finance',
  '/finance/scorecard',
  '/finance/data',
  '/finance/forecast',
  '/finance/reports',
  '/finance/budget',
  '/finance/dashboards',
] as const;

export type FinanceRoute = (typeof FINANCE_ROUTES)[number];

const KEY = 'fb-finance-last-view';

function isFinanceRoute(v: string | null): v is FinanceRoute {
  return v != null && (FINANCE_ROUTES as readonly string[]).includes(v);
}

/** Persist a finance path (ignored if it isn't a known finance route). */
export function writeLastFinanceView(path: string): void {
  try {
    if (isFinanceRoute(path)) localStorage.setItem(KEY, path);
  } catch {
    /* private browsing / unavailable — memory is best-effort */
  }
}

/** The last finance view, or `/finance` (Overview) when unset/invalid. */
export function readLastFinanceView(): FinanceRoute {
  try {
    const v = localStorage.getItem(KEY);
    // Forecast is switched off for now: never hand it back as the place to return to.
    if (isFinanceRoute(v) && !(v.startsWith('/finance/forecast') && !isForecastEnabled())) return v;
  } catch {
    /* ignore */
  }
  return '/finance';
}
